import React, { useState, useContext, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  ListItemText,
  Button,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
} from "@mui/material";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import Logout from "@mui/icons-material/Logout";
import BarChart from "@mui/icons-material/BarChart";
import LocationOn from "@mui/icons-material/LocationOn";
import Favorite from "@mui/icons-material/Favorite";
import Settings from "@mui/icons-material/Settings";
import Home from "@mui/icons-material/Home";
import Message from "@mui/icons-material/Message";
import CalendarToday from "@mui/icons-material/CalendarToday";
import Image from "@mui/icons-material/Image";
import Info from "@mui/icons-material/Info";
import MenuIcon from "@mui/icons-material/Menu";
import Monitor from "@mui/icons-material/Monitor";
import PhoneAndroid from "@mui/icons-material/PhoneAndroid";
import { useLocation, useNavigate } from "react-router";
import { UserStore } from "..";
import { useVendor } from "../provider/vendor-provider";
import VenueSwitcher, { NAV_COLORS } from "./venue-switcher";
import { CommandCentreVenue } from "../state/UserState";

export const HEADER_BAR_HEIGHT = 64;
export const NAV_BAR_HEIGHT = 44;
export const HEADER_HEIGHT_DESKTOP = HEADER_BAR_HEIGHT + NAV_BAR_HEIGHT;
export const HEADER_HEIGHT_MOBILE = HEADER_BAR_HEIGHT;

// The header is full-bleed but its content is constrained to the same measure as
// PageContainer (`mx-auto max-w-screen-xl px-4 lg:px-6`), so the logo, nav and
// user controls line up with the page body at every width. The breakpoint is
// Tailwind's lg (1024px), not MUI's, so the two containers stay in step.
const CONTENT_CONTAINER_SX = {
  width: "100%",
  maxWidth: "1280px",
  mx: "auto",
  px: "16px",
  "@media (min-width:1024px)": { px: "24px" },
} as const;

// Cancels a nav button's own horizontal padding so its label — not its hover
// box — sits on the content edge.
const NAV_ITEM_PX = 2;
const NAV_STRIP_INSET = "-16px";

type NavLink = {
  name: string;
  description: string;
  icon: typeof Message;
  path: string;
};

type NavGroup = {
  name: string;
  icon: typeof Message;
  path?: string;
  items?: NavLink[];
  // Only shown when the selected venue is the admin venue (see UserState.isAdmin).
  isAdminRequired?: boolean;
};

interface NavbarProps {
  selectedVenue?: CommandCentreVenue;
  userVenues?: CommandCentreVenue[];
  totalVenueCount: number;
  selectVenue: (venue: CommandCentreVenue) => void;
  venueSearchTerm?: string;
  updateVenueSearchTerm: (venueSearchTerm: string) => void;
  isSingleVenue: boolean;
}

const navigationItems: NavGroup[] = [
  {
    name: "Home",
    icon: Home,
    path: "/home",
  },
  {
    name: "Player Communications",
    icon: Message,
    items: [
      {
        name: "Player Messaging",
        description: "Send push notifications to players",
        icon: Message,
        path: "/player-communications",
      },
      {
        name: "Scheduled Notifications",
        description: "View upcoming player communications",
        icon: CalendarToday,
        path: "/scheduled-communications",
      },
    ],
  },
  {
    name: "Mobile App",
    icon: PhoneAndroid,
    items: [
      {
        name: "Promotional Banners",
        description: "Provide home screen promotional banners",
        icon: Image,
        path: "/mobile-banners",
      },
      {
        name: "Venue Event Images",
        description: "Provide images for venue events",
        icon: Image,
        path: "/mobile-attractors",
      },
      {
        name: "Additional Information Page",
        description: "Provide links, such as terms and conditions",
        icon: Info,
        path: "/mobile-about-us",
      },
    ],
  },
  {
    name: "Venue Kiosks",
    icon: Monitor,
    items: [
      {
        name: "Kiosk Promotional Images",
        description: "Provide images to display on venue kiosks",
        icon: Image,
        path: "/kiosk-attractors",
      },
    ],
  },
  {
    name: "Analytics",
    icon: BarChart,
    items: [
      {
        name: "App Analytics",
        description: "View mobile app statistics",
        icon: BarChart,
        path: "/analytics",
      },
      {
        name: "Geofence Analytics",
        description: "View geofence statistics",
        icon: LocationOn,
        path: "/geo-analytics",
      },
    ],
  },
  {
    name: "Administration",
    icon: Settings,
    isAdminRequired: true,
    items: [
      {
        name: "Venue Setup & Status",
        description: "View and manage venue setup status and configuration",
        icon: Settings,
        path: "/venue-setup-status",
      },
      {
        name: "Kiosk Attractor Health",
        description: "View the health status of all kiosks across venues",
        icon: Favorite,
        path: "/kiosk-health",
      },
      {
        name: "Geofence Creator",
        description: "Create, edit and delete geofences",
        icon: LocationOn,
        path: "/fence-config",
      },
      {
        name: "Venue Geofence Setup",
        description: "Assign geofences to venues",
        icon: Home,
        path: "/fence-venue-config",
      },
    ],
  },
];

const ModernNavbar: React.FC<NavbarProps> = ({
  selectedVenue,
  userVenues,
  totalVenueCount,
  selectVenue,
  venueSearchTerm,
  updateVenueSearchTerm,
  isSingleVenue,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const userStore = useContext(UserStore);
  const { getVendorTitle, getVendorLogo } = useVendor();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  // Admin-only groups are hidden unless the selected venue is the admin venue.
  const visibleNavigationItems = navigationItems.filter(
    (group) => !group.isAdminRequired || userStore.isAdmin
  );

  // Close any open menu when the route changes or Escape is pressed.
  useEffect(() => {
    setActiveDropdown(null);
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setActiveDropdown(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const handleLogout = () => {
    userStore.logout();
    navigate("/login");
  };

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleCloseDropdown = () => setActiveDropdown(null);

  const isGroupActive = (group: NavGroup) =>
    group.path
      ? pathname === group.path
      : (group.items ?? []).some((item) => pathname.startsWith(item.path));

  const goTo = (path: string) => {
    handleCloseDropdown();
    setMobileOpen(false);
    navigate(path);
  };

  const renderDropdownPanel = (group: NavGroup) => (
    <Box
      role="menu"
      sx={{
        position: "absolute",
        top: "100%",
        left: 0,
        zIndex: 1400,
        minWidth: 280,
        maxWidth: 340,
        backgroundColor: NAV_COLORS.panelBg,
        borderLeft: `3px solid ${NAV_COLORS.accent}`,
        boxShadow: "0 14px 32px rgba(0, 0, 0, 0.32)",
      }}
    >
      {group.items?.map((subItem, index) => (
        <Box
          key={subItem.path}
          role="menuitem"
          onClick={() => goTo(subItem.path)}
          sx={{
            px: 2.5,
            py: 1.5,
            cursor: "pointer",
            borderBottom:
              index < (group.items?.length ?? 0) - 1
                ? `1px solid ${NAV_COLORS.panelDivider}`
                : "none",
            backgroundColor: pathname.startsWith(subItem.path)
              ? NAV_COLORS.panelHover
              : "transparent",
            "&:hover": { backgroundColor: NAV_COLORS.panelHover },
          }}
        >
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: NAV_COLORS.panelTitle,
              lineHeight: 1.35,
            }}
          >
            {subItem.name}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: NAV_COLORS.panelDesc,
              lineHeight: 1.4,
              mt: 0.25,
            }}
          >
            {subItem.description}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const renderNavBar = () => (
    <Box
      component="nav"
      sx={{
        height: NAV_BAR_HEIGHT,
        backgroundColor: NAV_COLORS.navBg,
        borderTop: `1px solid ${NAV_COLORS.divider}`,
      }}
    >
      <Box
        sx={{
          ...CONTENT_CONTAINER_SX,
          height: "100%",
          display: "flex",
          alignItems: "stretch",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "stretch",
            mx: NAV_STRIP_INSET,
            minWidth: 0,
          }}
        >
          {visibleNavigationItems.map((group) => {
            const isOpen = activeDropdown === group.name;
            const isActive = isGroupActive(group);
            const isHighlighted = isOpen || isActive;

            return (
              <Box
                key={group.name}
                // Once a menu is open, hovering a sibling switches to it.
                onMouseEnter={() => {
                  if (activeDropdown && group.items)
                    setActiveDropdown(group.name);
                }}
                sx={{ position: "relative", display: "flex" }}
              >
                <Box
                  component="button"
                  type="button"
                  aria-haspopup={group.items ? "menu" : undefined}
                  aria-expanded={group.items ? isOpen : undefined}
                  onClick={() =>
                    group.items
                      ? setActiveDropdown(isOpen ? null : group.name)
                      : goTo(group.path as string)
                  }
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: NAV_ITEM_PX,
                    border: "none",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    color: isHighlighted
                      ? NAV_COLORS.textPrimary
                      : NAV_COLORS.navIdle,
                    backgroundColor: isHighlighted
                      ? NAV_COLORS.navActive
                      : "transparent",
                    transition: "background-color 0.15s ease, color 0.15s ease",
                    "&:hover": {
                      color: NAV_COLORS.textPrimary,
                      backgroundColor: isHighlighted
                        ? NAV_COLORS.navActive
                        : NAV_COLORS.navHover,
                    },
                    "&::after": isHighlighted
                      ? {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 2,
                          backgroundColor: NAV_COLORS.accent,
                        }
                      : undefined,
                  }}
                >
                  {group.name}
                  {group.items && (
                    <KeyboardArrowDown
                      sx={{
                        fontSize: 18,
                        transition: "transform 0.18s ease",
                        transform: isHighlighted ? "rotate(180deg)" : "none",
                      }}
                    />
                  )}
                </Box>

                {group.items && isOpen && renderDropdownPanel(group)}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );

  const renderMobileDrawer = () => (
    <Drawer
      variant="temporary"
      anchor="left"
      open={mobileOpen}
      onClose={handleDrawerToggle}
      ModalProps={{ keepMounted: true }}
      sx={{
        display: { xs: "block", md: "none" },
        "& .MuiDrawer-paper": {
          boxSizing: "border-box",
          width: 320,
          backgroundColor: NAV_COLORS.headerBg,
          color: NAV_COLORS.navIdle,
          backgroundImage: "none",
        },
      }}
    >
      <Box
        sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}
      >
        {/* Brand */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: "8px",
              overflow: "hidden",
              backgroundColor: "#000000",
              border: `1px solid ${NAV_COLORS.divider}`,
            }}
          >
            <img
              src={getVendorLogo()}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: NAV_COLORS.textPrimary,
                fontWeight: 700,
                fontSize: "1rem",
                lineHeight: 1.25,
              }}
            >
              {getVendorTitle()}
            </Typography>
            {selectedVenue && (
              <VenueSwitcher
                variant="drawer"
                isSingleVenue={isSingleVenue}
                selectedVenue={selectedVenue}
                userVenues={userVenues}
                totalVenueCount={totalVenueCount}
                selectVenue={selectVenue}
                venueSearchTerm={venueSearchTerm}
                updateVenueSearchTerm={updateVenueSearchTerm}
              />
            )}
          </Box>
        </Box>

        {/* User */}
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1.5,
            backgroundColor: NAV_COLORS.navActive,
            borderLeft: `3px solid ${NAV_COLORS.accent}`,
          }}
        >
          <Typography
            sx={{ fontSize: "0.8125rem", color: NAV_COLORS.textMuted }}
          >
            Welcome,{" "}
            <Box
              component="span"
              sx={{ color: NAV_COLORS.textPrimary, fontWeight: 700 }}
            >
              {userStore.user?.firstName}
            </Box>
          </Typography>
        </Box>

        {/* Navigation */}
        <Box sx={{ flexGrow: 1, overflow: "auto" }}>
          {visibleNavigationItems.map((group) => (
            <Box key={group.name} sx={{ mb: 1 }}>
              <Typography
                sx={{
                  color: NAV_COLORS.accent,
                  fontWeight: 600,
                  fontSize: "0.6875rem",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  px: 1,
                  mb: 0.5,
                }}
              >
                {group.name}
              </Typography>
              <List disablePadding>
                {(
                  group.items ?? [
                    {
                      name: group.name,
                      description: "",
                      icon: group.icon,
                      path: group.path as string,
                    },
                  ]
                ).map((subItem) => (
                  <ListItem key={subItem.path} disablePadding>
                    <ListItemButton
                      onClick={() => goTo(subItem.path)}
                      sx={{
                        py: 1,
                        borderLeft: pathname.startsWith(subItem.path)
                          ? `3px solid ${NAV_COLORS.accent}`
                          : "3px solid transparent",
                        backgroundColor: pathname.startsWith(subItem.path)
                          ? NAV_COLORS.navActive
                          : "transparent",
                        "&:hover": { backgroundColor: NAV_COLORS.navHover },
                      }}
                    >
                      <ListItemText
                        disableTypography
                        primary={
                          <Typography
                            sx={{
                              color: NAV_COLORS.textPrimary,
                              fontWeight: 500,
                              fontSize: "0.875rem",
                            }}
                          >
                            {subItem.name}
                          </Typography>
                        }
                        secondary={
                          subItem.description ? (
                            <Typography
                              sx={{
                                color: NAV_COLORS.textMuted,
                                fontSize: "0.75rem",
                              }}
                            >
                              {subItem.description}
                            </Typography>
                          ) : null
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </Box>

        {/* Logout */}
        <Box
          sx={{ mt: 2, pt: 2, borderTop: `1px solid ${NAV_COLORS.divider}` }}
        >
          <Button
            fullWidth
            onClick={handleLogout}
            startIcon={<Logout sx={{ fontSize: 18 }} />}
            sx={{
              justifyContent: "flex-start",
              textTransform: "none",
              color: NAV_COLORS.textPrimary,
              border: `1px solid rgba(255, 255, 255, 0.18)`,
              borderRadius: "6px",
              px: 2,
              py: 1,
              fontSize: "0.875rem",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.08)",
                borderColor: "rgba(255, 255, 255, 0.32)",
              },
            }}
          >
            Logout
          </Button>
        </Box>
      </Box>
    </Drawer>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: NAV_COLORS.headerBg,
          backgroundImage: "none",
          boxShadow: "none",
          zIndex: 1300,
        }}
      >
        {/* Top bar: brand, venue switcher, user */}
        <Toolbar
          disableGutters
          sx={{
            minHeight: `${HEADER_BAR_HEIGHT}px !important`,
            height: HEADER_BAR_HEIGHT,
          }}
        >
          <Box
            sx={{
              ...CONTENT_CONTAINER_SX,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            {isMobile && (
              <IconButton
                edge="start"
                aria-label="Open navigation"
                onClick={handleDrawerToggle}
                sx={{
                  color: NAV_COLORS.textPrimary,
                  "&:hover": { backgroundColor: NAV_COLORS.navHover },
                }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Brand + venue */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                minWidth: 0,
              }}
            >
              <Box
                onClick={() => navigate("/home")}
                sx={{
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  borderRadius: "8px",
                  overflow: "hidden",
                  cursor: "pointer",
                  backgroundColor: "#000000",
                  border: `1px solid ${NAV_COLORS.divider}`,
                }}
              >
                <img
                  src={getVendorLogo()}
                  alt="Logo"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  noWrap
                  onClick={() => navigate("/home")}
                  sx={{
                    color: NAV_COLORS.textPrimary,
                    fontWeight: 700,
                    fontSize: { xs: "0.9375rem", md: "1.0625rem" },
                    lineHeight: 1.25,
                    cursor: "pointer",
                  }}
                >
                  {getVendorTitle()}
                </Typography>
                {selectedVenue && (
                  <VenueSwitcher
                    isSingleVenue={isSingleVenue}
                    selectedVenue={selectedVenue}
                    userVenues={userVenues}
                    totalVenueCount={totalVenueCount}
                    selectVenue={selectVenue}
                    venueSearchTerm={venueSearchTerm}
                    updateVenueSearchTerm={updateVenueSearchTerm}
                  />
                )}
              </Box>
            </Box>

            {/* User + logout */}
            <Box
              sx={{
                ml: "auto",
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexShrink: 0,
              }}
            >
              <Typography
                noWrap
                sx={{
                  fontSize: "0.875rem",
                  color: NAV_COLORS.textMuted,
                  display: { xs: "none", sm: "block" },
                }}
              >
                Welcome,{" "}
                <Box
                  component="span"
                  sx={{ color: NAV_COLORS.textPrimary, fontWeight: 700 }}
                >
                  {userStore.user?.firstName}
                </Box>
              </Typography>

              <Button
                onClick={handleLogout}
                startIcon={<Logout sx={{ fontSize: 18 }} />}
                sx={{
                  textTransform: "none",
                  color: NAV_COLORS.textPrimary,
                  border: `1px solid rgba(255, 255, 255, 0.18)`,
                  borderRadius: "6px",
                  px: 1.75,
                  py: 0.625,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  minWidth: 0,
                  "& .MuiButton-startIcon": { mr: { xs: 0, sm: 1 } },
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                    borderColor: "rgba(255, 255, 255, 0.32)",
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{ display: { xs: "none", sm: "inline" } }}
                >
                  Logout
                </Box>
              </Button>
            </Box>
          </Box>
        </Toolbar>

        {/* Second row: primary navigation (desktop only) */}
        {!isMobile && renderNavBar()}
      </AppBar>

      {renderMobileDrawer()}

      {/* Click-away target for open dropdowns */}
      {activeDropdown && (
        <Box
          onClick={handleCloseDropdown}
          sx={{ position: "fixed", inset: 0, zIndex: 1200 }}
        />
      )}
    </>
  );
};

export default ModernNavbar;
